import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Users, Grid3X3, LineChartIcon, Info } from 'lucide-react';
import { cn, formatCompact } from '@/lib/utils';
import { useGA4CohortRetention } from '@/hooks/useGA4CohortData';
import { useAuthStore } from '@/stores/auth-store';

type ViewMode = 'heatmap' | 'linechart';

// Color palette for cohort lines (gradient from pink to blue)
const COHORT_COLORS = [
  '#ec4899', // pink-500
  '#f472b6', // pink-400
  '#c084fc', // purple-400
  '#a78bfa', // violet-400
  '#818cf8', // indigo-400
  '#60a5fa', // blue-400
  '#38bdf8', // sky-400
  '#22d3ee', // cyan-400
];

/**
 * Cohort Retention View - Dual visualization for user retention analysis
 * Shows how users acquired in each week return over subsequent weeks
 */
export function CohortRetentionView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;

  const [viewMode, setViewMode] = useState<ViewMode>('linechart');
  const [hoveredCohort, setHoveredCohort] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ cohort: string; week: number } | null>(null);

  const { data: cohortData, isLoading } = useGA4CohortRetention(8, 8);

  // Prepare line chart data
  const lineChartData = useMemo(() => {
    if (!cohortData?.cohorts) return [];

    // Find max weeks across all cohorts
    const maxWeeks = Math.max(
      ...cohortData.cohorts.flatMap(c => c.weeklyData.map(w => w.week))
    );

    // Create data points for each week
    const data = [];
    for (let week = 0; week <= maxWeeks; week++) {
      const point: Record<string, any> = { week: `W${week}` };

      for (const cohort of cohortData.cohorts) {
        const weekData = cohort.weeklyData.find(w => w.week === week);
        point[cohort.cohortId] = weekData?.retentionRate ?? null;
        point[`${cohort.cohortId}_users`] = weekData?.activeUsers ?? null;
      }

      data.push(point);
    }

    return data;
  }, [cohortData]);

  // Get retention color for heatmap (blue gradient)
  const getRetentionColor = (rate: number): string => {
    if (rate >= 80) return 'bg-blue-400';
    if (rate >= 60) return 'bg-blue-500';
    if (rate >= 40) return 'bg-blue-600';
    if (rate >= 20) return 'bg-blue-700';
    if (rate > 0) return 'bg-blue-800';
    return 'bg-muted/30';
  };

  const getRetentionOpacity = (rate: number): number => {
    return Math.max(0.3, rate / 100);
  };

  if (!hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Connect GA4 to see cohort retention</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Cohort Retention
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            User survival over weeks
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setViewMode('heatmap')}
            className={cn(
              'p-2 rounded-md transition-colors',
              viewMode === 'heatmap'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title="Heatmap View"
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('linechart')}
            className={cn(
              'p-2 rounded-md transition-colors',
              viewMode === 'linechart'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title="Line Chart View"
          >
            <LineChartIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main Visualization */}
      <div className="glass-panel p-6 flex-1 min-h-[400px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading cohort data...
          </div>
        ) : !cohortData?.cohorts || cohortData.cohorts.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No cohort data available</p>
              <p className="text-xs text-muted-foreground mt-1">
                Cohort analysis requires at least a few weeks of user data
              </p>
            </div>
          </div>
        ) : viewMode === 'linechart' ? (
          /* Line Chart View */
          <div className="h-full flex flex-col">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 15%)" />
                <XAxis
                  dataKey="week"
                  stroke="hsl(0 0% 45%)"
                  tick={{ fill: 'hsl(0 0% 65%)', fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  stroke="hsl(0 0% 45%)"
                  tick={{ fill: 'hsl(0 0% 65%)', fontSize: 12 }}
                />
                <Tooltip content={<LineChartTooltip cohortData={cohortData} />} />
                <Legend
                  wrapperStyle={{ paddingTop: 10 }}
                  onMouseEnter={(e: any) => setHoveredCohort(e.dataKey)}
                  onMouseLeave={() => setHoveredCohort(null)}
                />
                {cohortData.cohorts.map((cohort, index) => (
                  <Line
                    key={cohort.cohortId}
                    type="monotone"
                    dataKey={cohort.cohortId}
                    stroke={COHORT_COLORS[index % COHORT_COLORS.length]}
                    strokeWidth={hoveredCohort === cohort.cohortId ? 3 : 2}
                    dot={{ r: hoveredCohort === cohort.cohortId ? 5 : 3 }}
                    activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                    opacity={hoveredCohort && hoveredCohort !== cohort.cohortId ? 0.3 : 1}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          /* Heatmap View */
          <div className="h-full flex flex-col overflow-auto">
            <div className="min-w-[600px]">
              {/* Header row */}
              <div className="flex items-center mb-2">
                <div className="w-24 text-xs text-muted-foreground font-medium">Cohort</div>
                <div className="w-16 text-xs text-muted-foreground text-center font-medium">Users</div>
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i} className="flex-1 text-xs text-muted-foreground text-center font-medium">
                    W{i}
                  </div>
                ))}
              </div>

              {/* Cohort rows */}
              {cohortData.cohorts.map((cohort, cohortIndex) => (
                <div
                  key={cohort.cohortId}
                  className={cn(
                    'flex items-center py-1 rounded transition-colors',
                    hoveredCohort === cohort.cohortId && 'bg-muted/50'
                  )}
                  onMouseEnter={() => setHoveredCohort(cohort.cohortId)}
                  onMouseLeave={() => setHoveredCohort(null)}
                >
                  {/* Cohort label */}
                  <div className="w-24 flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: COHORT_COLORS[cohortIndex % COHORT_COLORS.length] }}
                    />
                    <span className="text-sm text-foreground font-medium">{cohort.cohortId}</span>
                  </div>

                  {/* Total users */}
                  <div className="w-16 text-sm text-muted-foreground text-center">
                    {formatCompact(cohort.totalUsers)}
                  </div>

                  {/* Week cells */}
                  {Array.from({ length: 8 }, (_, weekIndex) => {
                    const weekData = cohort.weeklyData.find(w => w.week === weekIndex);
                    const retention = weekData?.retentionRate ?? 0;
                    const isHovered = hoveredCell?.cohort === cohort.cohortId && hoveredCell?.week === weekIndex;

                    return (
                      <div key={weekIndex} className="flex-1 px-0.5">
                        <div
                          className={cn(
                            'relative h-10 rounded-sm flex items-center justify-center transition-all cursor-pointer',
                            getRetentionColor(retention),
                            isHovered && 'ring-2 ring-white'
                          )}
                          style={{ opacity: getRetentionOpacity(retention) }}
                          onMouseEnter={() => setHoveredCell({ cohort: cohort.cohortId, week: weekIndex })}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          <span className="text-xs font-medium text-white">
                            {retention > 0 ? `${retention.toFixed(0)}%` : '-'}
                          </span>

                          {/* Tooltip */}
                          {isHovered && weekData && (
                            <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 glass-panel p-2 shadow-lg border border-border whitespace-nowrap">
                              <p className="text-xs font-medium text-foreground">W{weekIndex}</p>
                              <p className="text-xs text-muted-foreground">
                                {cohort.cohortId}: {weekData.activeUsers}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border">
              <span className="text-xs text-muted-foreground">0%</span>
              {[20, 40, 60, 80, 100].map((rate) => (
                <div
                  key={rate}
                  className={cn('w-6 h-4 rounded-sm', getRetentionColor(rate))}
                  style={{ opacity: getRetentionOpacity(rate) }}
                />
              ))}
              <span className="text-xs text-muted-foreground">100%</span>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {cohortData?.cohorts && cohortData.cohorts.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <RetentionStatCard
            title="Week 1 Retention"
            value={calculateAvgRetention(cohortData.cohorts, 1)}
            description="Avg. users returning after 1 week"
            color="pink"
          />
          <RetentionStatCard
            title="Week 4 Retention"
            value={calculateAvgRetention(cohortData.cohorts, 4)}
            description="Avg. users returning after 1 month"
            color="purple"
          />
          <RetentionStatCard
            title="Best Cohort"
            value={findBestCohort(cohortData.cohorts)}
            description="Highest W1 retention"
            color="blue"
            isText
          />
          <RetentionStatCard
            title="Total Cohorts"
            value={cohortData.cohorts.length.toString()}
            description="Weeks of data analyzed"
            color="cyan"
            isText
          />
        </div>
      )}

      {/* Info Panel */}
      <div className="glass-panel p-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Understanding Cohort Retention</p>
            <p className="text-xs">
              Each cohort represents users acquired during a specific week. The retention rate shows
              what percentage of those users returned in subsequent weeks. Higher retention in later
              weeks indicates better user engagement and product stickiness.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function calculateAvgRetention(cohorts: Array<{ weeklyData: Array<{ week: number; retentionRate: number }> }>, week: number): string {
  const rates = cohorts
    .map(c => c.weeklyData.find(w => w.week === week)?.retentionRate)
    .filter((r): r is number => r !== undefined);

  if (rates.length === 0) return 'N/A';
  const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
  return `${avg.toFixed(1)}%`;
}

function findBestCohort(cohorts: Array<{ cohortId: string; weeklyData: Array<{ week: number; retentionRate: number }> }>): string {
  let best = { cohortId: 'N/A', rate: 0 };

  for (const cohort of cohorts) {
    const w1 = cohort.weeklyData.find(w => w.week === 1)?.retentionRate ?? 0;
    if (w1 > best.rate) {
      best = { cohortId: cohort.cohortId, rate: w1 };
    }
  }

  return best.cohortId;
}

interface RetentionStatCardProps {
  title: string;
  value: string;
  description: string;
  color: 'pink' | 'purple' | 'blue' | 'cyan';
  isText?: boolean;
}

function RetentionStatCard({ title, value, description, color, isText: _isText }: RetentionStatCardProps) {
  const colorClasses = {
    pink: 'text-pink-400',
    purple: 'text-purple-400',
    blue: 'text-blue-400',
    cyan: 'text-cyan-400',
  };

  return (
    <div className="glass-panel p-4">
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className={cn('text-2xl font-bold mt-1', colorClasses[color])}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

function LineChartTooltip({ active, payload, label, cohortData }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="glass-panel p-3 shadow-lg border border-border">
      <p className="font-medium text-foreground mb-2">{label}</p>
      <div className="space-y-1">
        {payload
          .filter((entry: any) => entry.value !== null)
          .sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
          .map((entry: any, index: number) => {
            const cohort = cohortData?.cohorts?.find((c: any) => c.cohortId === entry.dataKey);
            const users = cohort?.weeklyData?.find((w: any) => `W${w.week}` === label)?.activeUsers;

            return (
              <div key={index} className="flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-muted-foreground">{entry.dataKey}</span>
                </div>
                <div className="text-right">
                  <span style={{ color: entry.color }} className="font-medium">
                    {entry.value?.toFixed(1)}%
                  </span>
                  {users !== undefined && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({users})
                    </span>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
